import { isWithinInterval } from 'date-fns'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { createOrder, selectOrderStepIdsInRange } from '@/features/domain/order'
import { selectTerritoryLicensingLimits } from '@/features/domain/territory'
import { selectCalendarRange } from '@/features/domain/ui'
import { selectUserConfiguration } from '@/features/domain/user'

import { setMainSelection, useListApi, useResetEditingState } from '@/atoms'
import { formatDate, useNotification } from '@/hooks'
import { store, useAppDispatch } from '@/store'
import { dateUtils } from '@/utils'

import * as OrderTexts from '../../../../intl'

import { CustomNotification } from '../components/CustomNotification'
import { useOnChangeCalenderRange } from './useOnChangeCalenderRange'

export const useOnCreateOrder = () => {
  const dispatch = useAppDispatch()

  const stopEditing = useResetEditingState()

  const { planningRange } = useSelector(selectTerritoryLicensingLimits)
  const calendarRange = useSelector(selectCalendarRange)
  const userConfiguration = useSelector(selectUserConfiguration)

  const mounted = useRef<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const toast = useNotification()
  const { scrollTo } = useListApi('orderSteps')
  const { onChangeCalenderRange } = useOnChangeCalenderRange()

  const onCreateOrder = useCallback(
    async (createRequest: uui.domain.ui.forms.OrderCreate) => {
      try {
        setSubmitting(true)
        const thunkResult = await dispatch(createOrder(createRequest))

        if (mounted.current) {
          setSubmitting(false)
        }

        if (!createOrder.fulfilled.match(thunkResult)) {
          throw new Error(thunkResult.payload?.message ?? 'Internal error')
        }

        if (!mounted.current) return

        if (createRequest.type === 'recurrence') {
          const startDate = createRequest.startDate

          //check if the startdate is in the opened calendar range
          const isStartDateIsInOpenedCalendarRange = isWithinInterval(
            dateUtils.parseDate(startDate),
            {
              start: dateUtils.parseDate(calendarRange.minDate),
              end: dateUtils.parseDate(calendarRange.maxDate),
            },
          )

          const formattedStartDate = formatDate(startDate, false, userConfiguration)

          //display the toast with the link to navigate to the created order if the start date is outside the opened calendar range
          if (!isStartDateIsInOpenedCalendarRange) {
            toast.success(CustomNotification, {
              data: {
                formattedDate: formattedStartDate,
                onClick: () => onChangeCalenderRange(startDate, startDate),
              },
            })
          } else {
            toast.success(OrderTexts.recurrentOrderCreationSuccessToast(planningRange))
          }
        } else {
          toast.success(OrderTexts.singleOrderCreationSuccessToast())
        }

        stopEditing()

        const orderStepIdsInRange = selectOrderStepIdsInRange(store.getState())

        const selectableOrderStepIds = thunkResult.payload.filter(id => orderStepIdsInRange[id])

        setMainSelection('orderSteps', selectableOrderStepIds)

        if (selectableOrderStepIds.length === 0) return

        scrollTo(selectableOrderStepIds[0])
      } catch (e) {
        if (e instanceof Error) {
          toast.error(e.message)
        }
      }
    },
    [dispatch, stopEditing, scrollTo, toast],
  )

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  return {
    submitting,
    onCreateOrder,
  }
}
